import React from 'react';
import { AlertTriangle, Target, ClipboardList, Moon, TrendingUp, Wrench, Calendar, BarChart3, Brain, Compass } from '@/utils/icons';
import { LucideIcon } from 'lucide-react';

interface AIInsightsTabProps {
  athleteData: any;
  isDark: boolean;
}

const AIInsightsTab: React.FC<AIInsightsTabProps> = ({ athleteData, isDark }) => {
  const [activeMode, setActiveMode] = React.useState<'performance' | 'training'>('performance');
  
  const perfSummaryBullets: string[] = [
    `Readiness today: ${athleteData?.ai_insights?.readiness_level ?? 'N/A'}`,
    `Latest recovery score: ${athleteData?.recovery?.stats?.latest_recovery ?? 'N/A'}`,
    `Resting HR (latest): ${athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.resting_heart_rate ?? 'N/A'} bpm`,
    `HRV RMSSD (latest): ${athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.hrv_rmssd_milli ?? 'N/A'} ms`,
    `Sleep duration last night: ${athleteData?.sleep?.stats?.latest_duration ?? 'N/A'} hrs`,
    `Average strain last 7 days: ${athleteData?.activity?.stats?.average_strain ?? 'N/A'}`,
  ];
  const trainSummaryBullets: string[] = [
    `Macrocycle focus: ${athleteData?.ai_insights?.macrocycle_focus ?? 'Base/Build'}`,
    `Last 4 weeks avg strain: ${athleteData?.activity?.stats?.total_strain ?? 'N/A'}`,
    `Sleep efficiency trend: ${athleteData?.sleep?.stats?.average_quality ?? 'N/A'}`,
    `HRV trend: ${athleteData?.recovery?.stats?.recovery_trend ?? 'N/A'}`,
  ];

  const sectionCard = (title: string, items: string[], Icon?: LucideIcon) => (
    <div className={`cgm-card rounded-2xl p-6`}>
      <h4 className="text-xl font-bold theme-text-primary mb-4 flex items-center space-x-2">
        {Icon && <Icon className="w-5 h-5" />}
        <span>{title}</span>
      </h4>
      <ul className="space-y-3 list-disc pl-6">
        {items.map((it, i) => (
          <li key={i} className="theme-text-secondary leading-relaxed">{it}</li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="space-y-8">
      <div className={`cgm-card rounded-2xl p-2 flex w-full gap-2`}> 
        <button
          className={`cgm-tab flex-1 py-2 rounded-xl font-medium transition-all duration-200 ${
            activeMode === 'performance' ? 'cgm-tab-active' : ''
          }`}
          onClick={() => setActiveMode('performance')}
        >
          Performance
        </button>
        <button
          className={`cgm-tab flex-1 py-2 rounded-xl font-medium transition-all duration-200 ${
            activeMode === 'training' ? 'cgm-tab-active' : ''
          }`}
          onClick={() => setActiveMode('training')}
        >
          Training
        </button>
      </div>

      <div className={`cgm-card rounded-2xl p-6`}> 
        <h4 className="text-xl font-bold theme-text-primary mb-4 flex items-center space-x-2">
          <ClipboardList className="w-5 h-5" />
          <span>{activeMode === 'performance' ? 'Today\'s Performance Summary' : 'Training Block Overview'}</span>
        </h4>
        <ul className="space-y-2 list-disc pl-6">
          {(activeMode === 'performance' ? perfSummaryBullets : trainSummaryBullets).map((b, i) => (
            <li key={i} className="theme-text-secondary leading-relaxed">{b}</li>
          ))}
        </ul>
      </div>

      {athleteData.ai_insights.error ? (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6 border border-red-200/50 dark:border-red-700/50">
          <h4 className={`text-lg font-semibold ${isDark ? 'text-red-400' : 'text-red-600'} mb-2`}>AI Analysis Unavailable</h4>
          <p className="theme-text-secondary">{athleteData.ai_insights.error}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {activeMode === 'performance' ? (
            <>
              <div className="lg:col-span-2">
              {sectionCard('Athlete Recommendations', (() => {
                const latestRecovery = athleteData?.recovery?.stats?.latest_recovery as number | undefined;
                const lastSleepHrs = athleteData?.sleep?.stats?.latest_duration as number | undefined;
                const hrv = athleteData?.recovery?.recovery_data?.slice(-1)[0]?.hrv_rmssd_milli as number | undefined;
                const avgStrain = athleteData?.activity?.stats?.average_strain as number | undefined;

                const bullets: string[] = [];
                if (typeof latestRecovery === 'number') {
                  if (latestRecovery >= 66) {
                    bullets.push('Intensity: High day. Schedule quality work with controlled volume.');
                    bullets.push('Strength output: 85–90% of recent bests; hold RPE 7–8 for main sets.');
                  } else if (latestRecovery >= 40) {
                    bullets.push('Intensity: Moderate day. Prioritize technical quality and aerobic intervals.');
                    bullets.push('Strength output: 75–85% on primary lifts; RPE 6–7.');
                  } else {
                    bullets.push('Intensity: Low day. Keep sessions in Zone 1–2 with mobility.');
                    bullets.push('Strength output: 60–70% technique work only; RPE ≤ 6.');
                  }
                } else {
                  bullets.push('Intensity: Base on readiness. Start moderate and adjust by RPE.');
                }

                if (typeof latestRecovery === 'number' && latestRecovery >= 66) {
                  bullets.push('Nutrition: Higher carbohydrate availability pre-session.');
                } else if (typeof latestRecovery === 'number' && latestRecovery < 40) {
                  bullets.push('Nutrition: Emphasize anti-inflammatory foods and adequate protein.');
                } else {
                  bullets.push('Nutrition: Balanced intake; ensure pre-training carbs.');
                }

                if (typeof lastSleepHrs === 'number') {
                  if (lastSleepHrs < 7) bullets.push('Sleep note: Sub-7h; extend warm-up by 10–15 minutes.');
                  else bullets.push('Sleep note: Adequate sleep; proceed with planned progressions.');
                }

                if (typeof hrv === 'number' && typeof avgStrain === 'number') {
                  if (hrv < 25 && avgStrain > 12) bullets.push('HRV/Strain: Autonomic stress high. Favor aerobic maintenance.');
                  else bullets.push('HRV/Strain: Tolerable. Include brief high-quality efforts.');
                }

                if (typeof latestRecovery === 'number') {
                  if (latestRecovery >= 66) bullets.push('Push guidance: Progress load if mechanics are stable.');
                  else if (latestRecovery < 40) bullets.push('Push guidance: Keep reserve; end sets at technical breakdown.');
                }

                return bullets;
              })(), Brain)}
                  </div>
              {sectionCard('Game-Day Readiness', (() => {
                const latestRecovery = athleteData?.recovery?.stats?.latest_recovery as number | undefined;
                const rhr = athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.resting_heart_rate as number | undefined;
                const hrv = athleteData?.recovery?.recovery_data?.slice?.(-1)?.[0]?.hrv_rmssd_milli as number | undefined;
                const bullets: string[] = [];
                if (typeof latestRecovery === 'number') {
                  if (latestRecovery >= 66) bullets.push('You are primed for quality work.');
                  else if (latestRecovery >= 40) bullets.push('You can train productively, but cap high-intensity bouts.');
                  else bullets.push('Body is signaling low readiness. Keep intensity low.');
                } else {
                  bullets.push('Readiness unclear. Start conservatively.');
                }
                if (typeof rhr === 'number') {
                  if (rhr > 5 + (athleteData?.ai_insights?.rhr_baseline ?? rhr)) bullets.push('Elevated resting HR suggests fatigue.');
                  else bullets.push('Resting HR near baseline; cardiac readiness is adequate.');
                }
                if (typeof hrv === 'number') {
                  if (hrv < 25) bullets.push('Lower HRV indicates autonomic strain.');
                  else bullets.push('HRV is supportive of higher-quality intervals.');
                }
                if (bullets.length === 0) bullets.push('No readiness suggestions available yet.');
                return bullets;
              })(), Compass)}
              {sectionCard('Sleep Impact', (() => {
                const lastHrs = athleteData?.sleep?.stats?.latest_duration as number | undefined;
                const avgQual = athleteData?.sleep?.stats?.average_quality as number | undefined;
                const eff = athleteData?.sleep?.sleep_data?.slice?.(-1)?.[0]?.sleep_efficiency as number | undefined;
                const bullets: string[] = [];
                if (typeof lastHrs === 'number') {
                  if (lastHrs < 7) bullets.push('Sub-7h sleep: reduce sprint dose; extend warm-up.');
                  else bullets.push('Adequate sleep: proceed with planned progressions.');
                }
                if (typeof avgQual === 'number') {
                  if (avgQual < 70) bullets.push('Recent sleep quality is suppressed.');
                  else bullets.push('Sleep quality supports quality training.');
                }
                if (typeof eff === 'number') {
                  if (eff < 85) bullets.push('Sleep efficiency is low.');
                }
                if (bullets.length === 0) bullets.push('No sleep-based adjustments at this time.');
                return bullets;
              })(), Moon)}
              {sectionCard('Load & Strain', (() => {
                const avgStrain = athleteData?.activity?.stats?.average_strain as number | undefined;
                const totalStrain = athleteData?.activity?.stats?.total_strain as number | undefined;
                const hours = athleteData?.activity?.stats?.total_duration_hours as number | undefined;
                const bullets: string[] = [];
                if (typeof avgStrain === 'number') {
                  if (avgStrain >= 12) bullets.push('Recent load is high—quality-over-quantity session.');
                  else if (avgStrain >= 8) bullets.push('Moderate load—include one focused block.');
                  else bullets.push('Lower recent load—opportunity for progression.');
                }
                if (typeof hours === 'number' && hours > 8) bullets.push('Large weekly duration—guard against fatigue.');
                if (typeof totalStrain === 'number' && totalStrain > 90) bullets.push('Total strain elevated.');
                if (bullets.length === 0) bullets.push('No load adjustments recommended.');
                return bullets;
              })(), TrendingUp)}
              {sectionCard('Immediate Priorities', (
                athleteData?.ai_insights?.next_week_focus && athleteData.ai_insights.next_week_focus.length > 0
                  ? athleteData.ai_insights.next_week_focus.slice(0, 3).map((x: string, i: number) => `${i + 1}. ${x}`)
                  : ['No immediate priorities available.']
              ), Target)}
            </>
          ) : (
            <>
              <div className="lg:col-span-2">
              {sectionCard('Suggested Workload', (() => {
                const weekly = athleteData?.ai_insights?.weekly_sessions ?? '5';
                const keySessions = athleteData?.ai_insights?.key_sessions?.join(', ') ?? 'Primary strength, Aerobic threshold';
                const modality = athleteData?.ai_insights?.recovery_modality ?? 'Mobility, Sauna';
                const hrvTrend = athleteData?.recovery?.stats?.recovery_trend as number | undefined;

                const bullets: string[] = [];
                bullets.push(`Plan: ${weekly} sessions this week.`);
                bullets.push(`Key sessions: ${keySessions}.`);
                bullets.push(`Recovery modalities: ${modality}.`);
                if (typeof hrvTrend === 'number') {
                  if (hrvTrend < 0) bullets.push('Rationale: HRV trending down—reduce intensity.');
                  else bullets.push('Rationale: HRV stable/improving—progress main lifts.');
                }
                bullets.push('Nutrition: Aim 1.6–2.2 g/kg/day protein.');
                bullets.push('Strength output: Main lifts at 80–88% 1RM.');
                bullets.push('Push guidance: Use RPE 7–8 on key days.');
                return bullets;
              })(), Wrench)}
              </div>
              {sectionCard('Block Objectives', (() => {
                const macro = athleteData?.ai_insights?.macrocycle_focus ?? 'Base/Build';
                const meso = athleteData?.ai_insights?.mesocycle_week ?? '1';
                const micro = athleteData?.ai_insights?.microcycle_focus ?? 'Technique + Aerobic';
                const items = [
                  `Macrocycle focus "${macro}": prioritize aligned adaptations.`,
                  `Mesocycle week ${meso}: adjust peaks/deloads accordingly.`,
                  `Microcycle emphasis "${micro}": ensure targeted work.`,
                ];
                return items.length ? items : ['No block objectives available.'];
              })(), Calendar)}
              {sectionCard('Capacity & Readiness Trends', (() => {
                const hrvTrend = athleteData?.recovery?.stats?.recovery_trend as number | undefined;
                const sleepAvg = athleteData?.sleep?.stats?.average_duration as number | undefined;
                const strain28 = athleteData?.activity?.stats?.total_strain as number | undefined;
                const bullets: string[] = [];
                if (typeof hrvTrend === 'number') {
                  if (hrvTrend < 0) bullets.push('HRV trending down: bias toward aerobic base.');
                  else bullets.push('HRV stable/improving: cautiously progress main lifts.');
                }
                if (typeof sleepAvg === 'number') {
                  if (sleepAvg < 7) bullets.push('Monthly sleep average under 7h: elevate recovery blocks.');
                }
                if (typeof strain28 === 'number') {
                  if (strain28 > 350) bullets.push('28-day load is high: schedule strategic deload.');
                }
                if (bullets.length === 0) bullets.push('No readiness trends available.');
                return bullets;
              })(), BarChart3)}
              {sectionCard('Watch-outs', (
                (athleteData?.ai_insights?.areas_of_concern && athleteData.ai_insights.areas_of_concern.length > 0)
                  ? (athleteData.ai_insights.areas_of_concern as string[])
                  : ['No watch-outs at this time.']
              ), AlertTriangle)}
              {sectionCard('Immediate Priorities', (
                athleteData?.ai_insights?.next_week_focus && athleteData.ai_insights.next_week_focus.length > 0
                  ? (athleteData.ai_insights.next_week_focus as string[]).slice(0, 3).map((x: string, i: number) => `${i + 1}. ${x}`)
                  : ['No immediate priorities available.']
              ), Target)}
            </>
          )}
        </div>
      )}

      {(() => {
        const ts = athleteData?.ai_insights?.generated_at;
        const d = ts ? new Date(ts) : null;
        const isValid = d instanceof Date && !isNaN(d.getTime());
        if (!isValid) return null;
        return (
          <div className={`cgm-card rounded-2xl p-4`}>
            <div className="text-center text-sm theme-text-secondary">
              <span>Analysis generated: {d.toLocaleString()}</span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AIInsightsTab;
